import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { publicAPI } from '../../services/api'
import LoadingSpinner from '../../components/LoadingSpinner'
import ErrorMessage from '../../components/ErrorMessage'
import { useTheme } from '../../contexts/ThemeContext'
import { localizeApiDetail } from '../../i18n/apiError'
import { useLatest } from '../Bruktbil/useLatest'
import { HYBRID_COLORS, changeColor, int, pct, signed } from './drivstoff'
import { bucketLabeller, yearLabel } from './series'
import type { DrivstoffHybrid as Hybrid, FuelStats, HybridBase, HybridLeaf, Periods } from './types'

type Branch = { ladbar: HybridLeaf | null; basis: HybridBase | null }

const NO_BRANCH: Branch = { ladbar: null, basis: null }

/**
 * Teknisk › Drivstoff › Hybrid: the tree Hybrid → Ladbar / Ikke ladbar → Bensin / Diesel,
 * the two branches per month against last year, and the top 10 in the chosen branch (AC11).
 * «Ekskluder elbil» does not apply here.
 */
export default function DrivstoffHybrid({ queryKey, onPeriods }: {
  queryKey: string
  /** Reports the periods of the latest answer, for the explanation box */
  onPeriods: (periods: Periods) => void
}) {
  const { t } = useTranslation('teknisk')
  const { isDark } = useTheme()
  const [branch, setBranch] = useState<Branch>(NO_BRANCH)
  const [hybrid, setHybrid] = useState<Hybrid | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useLatest(alive => {
    const params = {
      ...JSON.parse(queryKey),
      ...(branch.ladbar ? { ladbar: branch.ladbar } : {}),
      ...(branch.basis ? { basis: branch.basis } : {}),
    }
    setLoading(true)
    setError(null)
    publicAPI.getDrivstoffHybrid(params)
      .then(res => {
        if (!alive()) return
        setHybrid(res.data)
        onPeriods(res.data.periods)
      })
      .catch(err => {
        if (alive()) setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('drivstoff.loadError'))
      })
      .finally(() => { if (alive()) setLoading(false) })
  }, [queryKey, branch.ladbar, branch.basis])

  const data = useMemo(() => {
    if (!hybrid) return []
    const label = bucketLabeller(hybrid.granularity)
    return hybrid.points.map(p => ({
      label: label(p.bucket),
      ladbar: p.count_ladbar,
      ikke_ladbar: p.count_ikke_ladbar,
      prev_ladbar: p.previous?.count_ladbar ?? null,
      prev_ikke_ladbar: p.previous?.count_ikke_ladbar ?? null,
    }))
  }, [hybrid])

  // Keep the last answer on screen while a branch click loads, so the tree does not jump
  if (loading && !hybrid) return <LoadingSpinner />
  if (error) return <ErrorMessage message={error} />
  if (!hybrid) return null

  const leafLabel = (k: HybridLeaf) => t(`drivstoff.categoryLong.${k}` as const)
  const baseLabel = (k: HybridBase) => t(`drivstoff.category.${k}` as const)
  const branchLabel = branch.ladbar
    ? branch.basis ? `${leafLabel(branch.ladbar)} · ${baseLabel(branch.basis)}` : leafLabel(branch.ladbar)
    : t('drivstoff.hybrid.allHybrids')
  const isSelected = (b: Branch) => b.ladbar === branch.ladbar && b.basis === branch.basis

  const change = (s: FuelStats) =>
    s.share_change_pp === null
      ? <span className="text-gray-400" title={t('drivstoff.noComparisonTitle')}>{t('drivstoff.vsLastYear')}: –</span>
      : <span className={changeColor(s.share_change_pp)}>{t('drivstoff.ppVsLastYear', { value: signed(s.share_change_pp) })}</span>

  const nodeClass = (selected: boolean) =>
    `w-full text-left rounded-lg border p-3 transition-colors ${
      selected
        ? 'border-primary-600 ring-2 ring-primary-600 dark:border-primary-400 dark:ring-primary-400'
        : 'border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-gray-800'
    }`

  const { current, previous } = hybrid.periods
  const year = yearLabel(current.start_date, current.end_date)
  const previousYear = yearLabel(previous.start_date, previous.end_date)
  const axisColor = isDark ? '#94a3b8' : '#64748b'
  const gridColor = isDark ? '#334155' : '#e2e8f0'
  const color = (k: HybridLeaf) => HYBRID_COLORS[k][isDark ? 'dark' : 'light']

  return (
    <div className={`space-y-6 ${loading ? 'opacity-60' : ''}`} data-testid="drivstoff-hybrid">
      <div className="card p-4 space-y-3">
        <button onClick={() => setBranch(NO_BRANCH)} aria-pressed={isSelected(NO_BRANCH)} className={nodeClass(isSelected(NO_BRANCH))}>
          <span className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <span className="font-semibold text-gray-900 dark:text-gray-100">{t('drivstoff.category.hybrid')}</span>
            <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">{int(hybrid.hybrid.count)}</span>
            <span className="text-sm text-gray-600 dark:text-gray-400">{t('drivstoff.hybrid.shareOfAll', { share: pct(hybrid.hybrid.share) })}</span>
            <span className="text-sm">{change(hybrid.hybrid)}</span>
          </span>
        </button>
        <p className="text-xs text-gray-500 dark:text-gray-400">{t('drivstoff.hybrid.chooseBranch')}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {hybrid.grener.map(g => {
            const leaf: Branch = { ladbar: g.kategori, basis: null }
            return (
              <div key={g.kategori} className="space-y-2" data-testid={`drivstoff-hybrid-${g.kategori}`}>
                <button onClick={() => setBranch(leaf)} aria-pressed={isSelected(leaf)} className={nodeClass(isSelected(leaf))}>
                  <span className="flex items-center gap-2 font-semibold text-gray-900 dark:text-gray-100">
                    <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: color(g.kategori) }} aria-hidden="true" />
                    {leafLabel(g.kategori)}
                  </span>
                  <span className="flex flex-wrap items-baseline gap-x-3 mt-1">
                    <span className="text-xl font-bold text-gray-900 dark:text-gray-100">{int(g.count)}</span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t('drivstoff.hybrid.shareOfHybrid', { share: pct(g.share) })}</span>
                    <span className="text-sm">{change(g)}</span>
                  </span>
                </button>
                <div className="grid grid-cols-2 gap-2 pl-4 border-l-2 border-gray-200 dark:border-dark-border">
                  {g.basis.filter(b => b.count > 0 || b.basis !== 'annet').map(b => {
                    const node: Branch = { ladbar: g.kategori, basis: b.basis }
                    return (
                      <button
                        key={b.basis}
                        onClick={() => setBranch(node)}
                        aria-pressed={isSelected(node)}
                        className={nodeClass(isSelected(node))}
                        data-testid={`drivstoff-hybrid-${g.kategori}-${b.basis}`}
                      >
                        <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">{baseLabel(b.basis)}</span>
                        <span className="block text-lg font-bold text-gray-900 dark:text-gray-100">{int(b.count)}</span>
                        <span className="block text-xs text-gray-600 dark:text-gray-400">{t('drivstoff.hybrid.shareOfBranch', { share: pct(b.share) })}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <div className="card p-4" data-testid="drivstoff-hybrid-chart">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
          {hybrid.granularity === 'month' ? t('drivstoff.hybrid.chartTitleMonthly') : t('drivstoff.hybrid.chartTitleDaily')}
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          {hybrid.previous_available ? t('drivstoff.hybrid.chartLegendHint') : t('drivstoff.hybrid.chartNoComparison')}
        </p>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis dataKey="label" stroke={axisColor} fontSize={12} />
              <YAxis tickFormatter={int} stroke={axisColor} fontSize={12} width={56} />
              <Tooltip formatter={(v: number) => int(v)} />
              <Legend />
              {(['ladbar', 'ikke_ladbar'] as const).map(k => (
                <Line key={k} type="monotone" dataKey={k}
                  name={t('drivstoff.hybrid.seriesName', { category: t(`drivstoff.category.${k}` as const), year })}
                  stroke={color(k)} strokeWidth={2} dot={false} connectNulls />
              ))}
              {hybrid.previous_available && (['ladbar', 'ikke_ladbar'] as const).map(k => (
                <Line key={`prev_${k}`} type="monotone" dataKey={`prev_${k}`}
                  name={t('drivstoff.hybrid.seriesName', { category: t(`drivstoff.category.${k}` as const), year: previousYear })}
                  stroke={color(k)} strokeDasharray="6 4" strokeOpacity={0.6} dot={false} connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {(['brands', 'models'] as const).map(view => (
          <div key={view} className="card p-4" data-testid={view === 'brands' ? 'drivstoff-hybrid-top-brands' : 'drivstoff-hybrid-top-models'}>
            <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {view === 'brands' ? t('drivstoff.hybrid.topBrands') : t('drivstoff.hybrid.topModels')} – {branchLabel}
            </h4>
            <div className="overflow-x-auto">
              <table className="data-table data-table-compact">
                <thead>
                  <tr>
                    <th className="w-8">#</th>
                    <th>{view === 'brands' ? t('drivstoff.colBrand') : t('drivstoff.colModel')}</th>
                    <th className="text-right">{t('drivstoff.colCount')}</th>
                    <th className="text-right">{t('drivstoff.colShare', { category: branchLabel })}</th>
                  </tr>
                </thead>
                <tbody>
                  {view === 'brands'
                    ? hybrid.top_brands.map((b, i) => (
                      <tr key={`${b.brand}-${i}`}>
                        <td>{i + 1}</td>
                        <td>{b.brand}</td>
                        <td className="text-right">{int(b.count)}</td>
                        <td className="text-right">{pct(b.share)}</td>
                      </tr>
                    ))
                    : hybrid.top_models.map((m, i) => (
                      <tr key={m.master_model_id}>
                        <td>{i + 1}</td>
                        <td><span className="text-gray-500 dark:text-gray-400">{m.brand}</span> {m.model}</td>
                        <td className="text-right">{int(m.count)}</td>
                        <td className="text-right">{pct(m.share)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
